import { Component, ViewChild, OnDestroy, inject, effect, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { switchMap, filter, Subscription, interval, take } from 'rxjs';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { NgStyle, DatePipe } from '@angular/common';
import { CdrService } from 'src/app/services/centrex/cdr.service';
import { ThemesService } from 'src/app/services/themes.service';
import { Router } from '@angular/router';
import { VoiceService } from 'src/app/services/call/voice.service';
import { Voice } from 'src/app/models/call/voice';
import { DialogDeleteConfirmationComponent } from '../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';

import {
    ApexAxisChartSeries,
    ApexChart,
    ChartComponent,
    ApexDataLabels,
    ApexXAxis,
    ApexPlotOptions,
    ApexStroke,
    ApexTitleSubtitle,
    ApexYAxis,
    ApexTooltip,
    ApexFill,
    ApexLegend,
    ApexTheme,
    NgApexchartsModule,
} from 'ng-apexcharts';
import { NumberInput } from '@angular/cdk/coercion';
import { MatListItemIcon } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
    tooltip: ApexTooltip;
    fill: ApexFill;
    legend: ApexLegend;
    theme: ApexTheme;
};

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    imports: [
        FaIconComponent,
        MatProgressBar,
        MatIconButton,
        MatIcon,
        NgApexchartsModule,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatListItemIcon,
        NgStyle,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        DatePipe,
        TranslateModule,
    ]
})
export class DashboardComponent implements OnDestroy {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    @ViewChild('chartExtensions') chartExtensions: ChartComponent;
    public chartExtensionsOptions: Partial<ChartOptions>;

    @ViewChild('chartCalls') chartCalls: ChartComponent;
    public chartCallsOptions: Partial<ChartOptions>;

    private _updater: Subscription;

    private _rangeUpdater: number[];

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = ['time', 'caller', 'state', 'called', 'error'];

    calls: MatTableDataSource<Voice>;

    cachePercent: NumberInput;

    constructor(
        private extensionService: ExtensionService,
        private cdrService: CdrService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private spinner: NgxSpinnerService,
        private themesService: ThemesService,
        private voiceService: VoiceService
    ) {
        this._rangeUpdater = [0, 60];
        this._updater = interval(1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((val) => {
                this._rangeUpdater[0]++;
                if (this._rangeUpdater[0] >= this._rangeUpdater[1]) {
                    this._rangeUpdater[0] = 0;
                    console.log('Auto-Refreshing data');
                    this.loadDatas();
                }

                this.cachePercent = (100 * (this._rangeUpdater[0] + 1)) / this._rangeUpdater[1];
            });

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
            }
        });

        this.chartExtensionsOptions = {
            series: [],
            chart: {
                type: 'bar',
                height: 240,
                stacked: true,
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                },
            },
            xaxis: {
                categories: ['Utilisateurs', 'Périphériques'],
            },
            tooltip: {
                theme: 'dark',
            },
            fill: {
                opacity: 1,
            },
            theme: {
                mode: this.themesService.getCurrentTheme() === 'avm-dark-theme' ? 'dark' : 'light',
            },
        };

        this.chartCallsOptions = {
            series: [],
            chart: {
                type: 'bar',
                height: 320,
                zoom: {
                    autoScaleYaxis: true,
                },
                stacked: true,
            },
            tooltip: {
                theme: 'dark',
            },
            yaxis: {
                min: 0,
            },
            xaxis: {
                type: 'datetime',
                min: new Date().setHours(new Date().getHours() - 96),
                tickAmount: 6,
            },
            theme: {
                mode: this.themesService.getCurrentTheme() === 'avm-dark-theme' ? 'dark' : 'light',
            },
        };

        this.cachePercent = 0;
    }

    ngOnDestroy(): void {
        this._updater.unsubscribe();
    }

    loadDatas() {
        this.loadCdrs();
        this.loadExtensions();
        this.loadCalls();
    }

    loadExtensions() {
        this.spinner.show();

        this.extensionService
            .getExtensions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (extensions) => {
                    this.spinner.hide();

                    this.chartExtensionsOptions.series = [
                        {
                            name: 'Enregistrés',
                            color: '#00DD00',
                            data: [
                                //user
                                extensions.filter((e) => e.type == 'user' && e.registrationState == 'registered').length,
                                //device
                                extensions.filter((e) => e.type == 'device' && e.registrationState == 'registered').length,
                            ],
                        },
                        {
                            name: 'Non enregistrés',
                            color: '#DD0000',
                            data: [
                                //user
                                extensions.filter((e) => e.type == 'user' && e.registrationState != 'registered').length,
                                //device
                                extensions.filter((e) => e.type == 'device' && e.registrationState != 'registered').length,
                            ],
                        },
                    ];
                },
                error: (error) => {
                    this.spinner.hide();
                },
            });
    }

    loadCdrs() {
        this.spinner.show();

        var yesterday = new Date();
        yesterday.setHours(yesterday.getHours() - 96);
        yesterday.setMinutes(0);
        yesterday.setSeconds(0);
        yesterday.setMilliseconds(0);

        var today = new Date();
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);

        this.cdrService
            .getCdrs(yesterday, today)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (cdrs) => {
                    this.spinner.hide();

                    this.chartCallsOptions.series = [];

                    //external calls
                    var externalCalls = cdrs
                        .filter((c) => c.callerId == null || c.calledId == null)
                        .map((c) => {
                            var xValue = new Date(c.setupTime);
                            xValue.setMinutes(0);
                            xValue.setSeconds(0);
                            xValue.setMilliseconds(0);
                            return {
                                x: xValue.getTime(),
                                y: 1,
                            };
                        });
                    //fill empty values
                    for (var dt = yesterday; externalCalls.length > 0 && dt < today; dt.setHours(dt.getHours() + 1)) {
                        if (externalCalls.filter((r) => r.x == dt.getTime()).length <= 0) {
                            externalCalls.push({
                                x: dt.getTime(),
                                y: null,
                            });
                        }
                    }

                    var externalCallsSerie = {
                        name: 'Appels externes',
                        color: '#0000DD',
                        data: [],
                    };

                    externalCalls.reduce(function (res, value) {
                        if (!res[value.x]) {
                            res[value.x] = { x: value.x, y: 0 };
                            externalCallsSerie.data.push(res[value.x]);
                        }
                        res[value.x].y += value.y;
                        return res;
                    }, {});

                    this.chartCallsOptions.series.push(externalCallsSerie);

                    //internal calls
                    var internalCalls = cdrs
                        .filter((c) => c.callerId != null && c.calledId != null)
                        .map((c) => {
                            var xValue = new Date(c.setupTime);
                            xValue.setMinutes(0);
                            xValue.setSeconds(0);
                            xValue.setMilliseconds(0);
                            return {
                                x: xValue.getTime(),
                                y: 1,
                            };
                        });
                    //fill empty values
                    for (var dt = yesterday; internalCalls.length > 0 && dt < today; dt.setHours(dt.getHours() + 1)) {
                        if (internalCalls.filter((r) => r.x == dt.getTime()).length <= 0) {
                            internalCalls.push({
                                x: dt.getTime(),
                                y: null,
                            });
                        }
                    }

                    var internalCallsSerie = {
                        name: 'Appels internes',
                        color: '#00DD00',
                        data: [],
                    };

                    internalCalls.reduce(function (res, value) {
                        if (!res[value.x]) {
                            res[value.x] = { x: value.x, y: 0 };
                            internalCallsSerie.data.push(res[value.x]);
                        }
                        res[value.x].y += value.y;
                        return res;
                    }, {});

                    this.chartCallsOptions.series.push(internalCallsSerie);
                },
                error: (error) => {
                    this.spinner.hide();
                },
            });
    }

    loadCalls() {
        this.spinner.show();

        this.voiceService
            .getCalls()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (calls) => {
                    this.calls = new MatTableDataSource(calls);
                    this.spinner.hide();
                },
                error: (error) => {
                    this.spinner.hide();
                },
            });
    }

    onReleaseCall(callId: string) {
        var confirmDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        confirmDialog.componentInstance.confirmMessage = this.translate.instant('Dashboard.ConfirmReleaseCall');

        confirmDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.voiceService.releaseCall(callId)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(this.translate.instant('Dashboard.ReleaseCallSuccess'));
                    this.loadCalls();
                },
                error: () => {
                    this.toastr.error(this.translate.instant('Dashboard.ReleaseCallFailed'));
                },
                complete: () => {
                    confirmDialog = null;
                },
            });
    }
}
